import { RefObject, useEffect, useRef } from 'react';
import {
  attachLiquidGlass,
  GlassHandle,
  LiquidGlassConfig,
  VARIANT_PRESETS,
  GlassVariantKey,
} from './liquidGlass';

interface UseLiquidGlassArgs {
  /** Outer surface element. */
  ref: RefObject<HTMLElement | null>;
  /** Mirror layer — has the page bg painted via background-attachment: fixed.
   *  The hook applies inline `filter: url(#id)` to this layer for the
   *  actual liquid-glass displacement + specular rim. */
  mirrorRef: RefObject<HTMLElement | null>;
  /** Tint layer (gets background tint + inset shadow). */
  tintRef: RefObject<HTMLElement | null>;
  variant?: GlassVariantKey;
  /** Per-element overrides on top of the variant preset. */
  override?: Partial<LiquidGlassConfig>;
  /** Optional radius override (otherwise reads computed border-radius). */
  radius?: number;
  /** Disable the effect (renders without the SVG filter). */
  disabled?: boolean;
}

/**
 * Attach the liquid-glass refraction effect to a DOM element. Handles
 * initial paint, ResizeObserver-driven rebuilds, and cleanup on unmount.
 *
 * The caller must provide refs to the outer surface and the two pre-
 * rendered child layers (mirror + tint) so React owns the DOM tree.
 */
export function useLiquidGlass({
  ref,
  mirrorRef,
  tintRef,
  variant = 'default',
  override,
  radius,
  disabled,
}: UseLiquidGlassArgs) {
  const handleRef = useRef<GlassHandle | null>(null);
  const cfgRef = useRef<LiquidGlassConfig>({
    ...VARIANT_PRESETS[variant],
    ...override,
  });
  cfgRef.current = { ...VARIANT_PRESETS[variant], ...override };

  useEffect(() => {
    if (disabled) return;
    const el = ref.current;
    const mirror = mirrorRef.current;
    const tint = tintRef.current;
    if (!el || !mirror || !tint) return;

    handleRef.current = attachLiquidGlass(
      el,
      { mirror, tint },
      () => cfgRef.current,
      { radius },
    );

    return () => {
      handleRef.current?.destroy();
      handleRef.current = null;
    };
  }, [disabled, radius]);

  useEffect(() => {
    handleRef.current?.update();
  }, [variant, override]);
}
